import { type ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';

import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { decodeJwt, getAccessToken } from '@/lib/api-client';
import { activeUnitForUser } from '@/lib/crm-active-unit';

// Specific-file import (not the feature barrel) to keep the layout → gate →
// hooks chain cycle-free, same as useBusinessLines.
import { useSelectedUnit } from '../hooks/useSelectedUnit';

interface WorkspaceChoiceGateProps {
  children: ReactNode;
}

/**
 * App-shell workspace gate: "every login starts with a choice", enforced at the
 * shell instead of only on the /crm index. A multi-unit CRM member who hasn't
 * picked a workspace THIS login session is routed to the chooser before any
 * page renders — whether they landed on /dashboard after signing in or
 * deep-linked straight into /crm/leads (which used to bypass the /crm entry
 * gate and silently display the default unit in the breadcrumb).
 *
 * Everyone else passes straight through: single-unit members (auto-entered),
 * all-units admins (their entry is the control tower, on CRM entry), users
 * without CRM access (never queried), and anyone already on the chooser.
 * The original destination is preserved via `?next=` and resumed after the pick.
 */
export function WorkspaceChoiceGate({ children }: WorkspaceChoiceGateProps) {
  const { hasPermission } = usePermissions();
  const location = useLocation();

  // Mirror of the my-units route permission (held by every CRM role) — a user
  // without CRM access must never trigger the units query from, say, /dashboard.
  const hasCrmAccess = hasPermission('crm:access:read:tenant');
  const onChooser = location.pathname === '/crm/choose-workspace';

  if (!hasCrmAccess || onChooser) return <>{children}</>;
  return <UnitAwareGate>{children}</UnitAwareGate>;
}

/** Inner component so the units query only mounts for CRM-capable users. */
function UnitAwareGate({ children }: WorkspaceChoiceGateProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { units, reach, hasValidStoredPick, isLoading } = useSelectedUnit();

  // Fast path: an epoch-stamped pick already stored for this user means the
  // choice was made this login session — render immediately and let the units
  // query validate it in the background (a since-deactivated unit still gets
  // caught and re-routed on resolve).
  const userId = decodeJwt(getAccessToken() ?? '')?.sub ?? null;
  const hadPick = !!activeUnitForUser(userId);

  const needsChoice = !isLoading && reach === 'MEMBER' && units.length > 1 && !hasValidStoredPick;

  useEffect(() => {
    if (needsChoice) {
      navigate({
        to: '/crm/choose-workspace',
        search: { next: location.href },
        replace: true,
      });
    }
  }, [needsChoice, navigate, location.href]);

  if (needsChoice || (isLoading && !hadPick)) {
    // Full-viewport: the gate mounts ABOVE the dashboard shell, so there is no
    // chrome behind this loader — the app only appears once the choice stands.
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <>{children}</>;
}
